import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import {
  Paper,
  Grid,
  TextField,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Collapse,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  MenuItem,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SyncIcon from '@mui/icons-material/Sync';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.css';
import Info from '../../utils/Alert/Info';
import RestrictionInfo from '../../utils/Alert/RestrictionInfo';
import { SUBSCRIPTION_TYPE } from '../../../const';
import { ShopContext } from '../../../Context/ShopContext';
import DateTimeHelpers from '../../../helpers/DateTimeHelpers';
import StockReportService from '../../../services/StockReportService';
import KitchenService from '../../../services/KitchenService';
import WarehouseService from '../../../services/WarehouseService';
import withConsoleBase from '../../utils/ConsoleBase/withConsoleBase';
import DateTimeRange from '../../utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from '../../../services/DynamicDateRangeService';
import { roundOff } from 'helpers/NumberFormatHelper';
import Grayout from '../../utils/GrayOut/Grayout';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';
import { AlertMessagesContext } from 'react-alert-messages';

function SubitemConsumptionReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [openCardId, setOpenCardId] = useState({});
  const [kitchens, setKitchens] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedKitchenId, setSelectedKitchenId] = useState('');
  const [prevFilterData, setPrevFilterData] = useState({
    fromDate,
    toDate,
  });

  const subscriptionType = shop?.subscription?.type;
  const [efficiencyFilter, setEfficiencyFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');
  
  const isWarehouseActive = shop && !featureIsExpired(shop?.warehouse_validity);

  const fetchKitchens = useCallback(async () => {
    try {
      const response = await KitchenService.getKitchens();
      setKitchens(response);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
  }, [postAlertMessage]);

  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await WarehouseService.getWarehouses();
      setWarehouses(response);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
  }, [postAlertMessage]);

  useEffect(() => {
    if (isWarehouseActive) {
      fetchKitchens();
      fetchWarehouses();
    }
  }, [isWarehouseActive, fetchKitchens, fetchWarehouses]);

  const getWarehouseName = useCallback(
    (warehouseId) => {
      if (!warehouseId) return 'Main Store / General';
      const found = warehouses.find((w) => String(w.id) === String(warehouseId));
      if (found) return found.name;
      const kitchenMatch = kitchens.find(
        (k) => String(k.warehouse?.id) === String(warehouseId)
      );
      if (kitchenMatch?.warehouse?.name) return kitchenMatch.warehouse.name;
      return `Warehouse #${warehouseId}`;
    },
    [warehouses, kitchens]
  );

  const getIsWithinEfficiency = useCallback((item) => {
    const diff = (item.total_expected || 0) - (item.total_used || 0);
    return diff >= -0.00001;
  }, []);

  const loadReport = async ({ _fromDate = fromDate, _toDate = toDate } = {}) => {
    setLoadingIndicator(true);
    try {
      const data = {
        filter_start_time: DateTimeHelpers.getTimezoneOffsetDate(_fromDate),
        filter_end_time: DateTimeHelpers.getTimezoneOffsetDate(_toDate),
      };
      const resp = await StockReportService.getSubitemConsumptionReport(data);
      setReportData(resp?.data || []);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const generateReport = () => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('SUBITEM_CONSUMPTION_REPORT');

    setFromDate(dynamicStartDate);
    setToDate(dynamicEndDate);

    loadReport({
      _fromDate: dynamicStartDate,
      _toDate: dynamicEndDate,
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      fromDate: dynamicStartDate,
      toDate: dynamicEndDate,
    });
  };

  const isAnyChangeOnReportFilters = useCallback(() => {
    return (
      !DateTimeHelpers.isSameDate(fromDate, prevFilterData.fromDate) ||
      !DateTimeHelpers.isSameDate(toDate, prevFilterData.toDate)
    );
  }, [fromDate, toDate, prevFilterData.fromDate, prevFilterData.toDate]);

  useEffect(() => {
    generateReport();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportFilters());
  }, [isAnyChangeOnReportFilters, isShowGenerateButton]);

  const toggleCard = (id) => {
    setOpenCardId((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const filteredData = reportData
    .map((item) => {
      const subitemCost = parseFloat(item.subitem_cost) || 0;

      if (!selectedKitchenId || selectedKitchenId === 'all') {
        const totalExpected = (item.combo_items || []).reduce(
          (sum, combo) => sum + (parseFloat(combo.expected_consumption) || 0),
          0
        );
        const totalUsed = (item.consumptions || []).reduce(
          (sum, c) => sum + (parseFloat(c.consumed_qty) || 0),
          0
        );
        const totalDamage = (item.consumptions || []).reduce(
          (sum, c) => sum + (parseFloat(c.damage_qty) || 0),
          0
        );
        const totalDamageCost = (item.consumptions || []).reduce(
          (sum, c) => sum + (parseFloat(c.damage_value) || 0),
          0
        );
        const difference = totalExpected - totalUsed;
        const expectedCost = totalExpected * subitemCost;
        const actualCost = totalUsed * subitemCost;
        const costDifference = expectedCost - actualCost;

        return {
          ...item,
          total_expected: totalExpected,
          total_used: totalUsed,
          total_damage: totalDamage,
          total_damage_cost: totalDamageCost,
          difference: difference,
          subitem_cost: subitemCost,
          expected_cost: expectedCost,
          actual_cost: actualCost,
          cost_difference: costDifference,
        };
      }

      const filteredCombos = (item.combo_items || []).filter(
        (combo) => String(combo.kitchen?.id) === String(selectedKitchenId)
      );
      if (filteredCombos.length === 0) {
        return null;
      }

      const totalExpected = filteredCombos.reduce(
        (sum, combo) => sum + (parseFloat(combo.expected_consumption) || 0),
        0
      );

      const selectedKitchenObj = kitchens.find((k) => String(k.id) === String(selectedKitchenId));
      const kitchenWarehouseId =
        selectedKitchenObj?.warehouse?.id ||
        selectedKitchenObj?.warehouse_id ||
        filteredCombos.find((c) => c.kitchen?.warehouse?.id)?.kitchen?.warehouse?.id;

      const matchingConsumptions = (item.consumptions || []).filter(
        (c) => (kitchenWarehouseId ? String(c.warehouse_id) === String(kitchenWarehouseId) : false)
      );

      const totalUsed = matchingConsumptions.reduce(
        (sum, c) => sum + (parseFloat(c.consumed_qty) || 0),
        0
      );
      const totalDamage = matchingConsumptions.reduce(
        (sum, c) => sum + (parseFloat(c.damage_qty) || 0),
        0
      );
      const totalDamageCost = matchingConsumptions.reduce(
        (sum, c) => sum + (parseFloat(c.damage_value) || 0),
        0
      );

      const difference = totalExpected - totalUsed;
      const expectedCost = totalExpected * subitemCost;
      const actualCost = totalUsed * subitemCost;
      const costDifference = expectedCost - actualCost;

      return {
        ...item,
        combo_items: filteredCombos,
        consumptions: matchingConsumptions,
        total_expected: totalExpected,
        total_used: totalUsed,
        total_damage: totalDamage,
        total_damage_cost: totalDamageCost,
        difference: difference,
        subitem_cost: subitemCost,
        expected_cost: expectedCost,
        actual_cost: actualCost,
        cost_difference: costDifference,
      };
    })
    .filter(Boolean)
    .filter((item) => {
      const searchLower = filterText.toLowerCase();
      const matchesSearch =
        !filterText ||
        (item.subitem_name || '').toLowerCase().includes(searchLower) ||
        (Array.isArray(item.combo_items) &&
          item.combo_items.some(
            (combo) =>
              (combo.combo_name || '').toLowerCase().includes(searchLower) ||
              (combo.kitchen?.name || '').toLowerCase().includes(searchLower)
          ));

      const isWithinEfficiency = getIsWithinEfficiency(item);
      const matchesEfficiency =
        efficiencyFilter === 'all' ||
        (efficiencyFilter === 'within' && isWithinEfficiency) ||
        (efficiencyFilter === 'outside' && !isWithinEfficiency);

      return matchesSearch && matchesEfficiency;
    })
    .sort((a, b) => {
      if (sortBy === 'name_asc') {
        return (a.subitem_name || '').localeCompare(b.subitem_name || '');
      }
      if (sortBy === 'name_desc') {
        return (b.subitem_name || '').localeCompare(a.subitem_name || '');
      }
      if (sortBy === 'expected_desc') {
        return (b.total_expected || 0) - (a.total_expected || 0);
      }
      if (sortBy === 'expected_asc') {
        return (a.total_expected || 0) - (b.total_expected || 0);
      }
      if (sortBy === 'actual_desc') {
        return (b.total_used || 0) - (a.total_used || 0);
      }
      if (sortBy === 'actual_asc') {
        return (a.total_used || 0) - (b.total_used || 0);
      }
      if (sortBy === 'diff_desc') {
        return (b.difference || 0) - (a.difference || 0);
      }
      if (sortBy === 'diff_asc') {
        return (a.difference || 0) - (b.difference || 0);
      }
      if (sortBy === 'expected_cost_desc') {
        return (b.expected_cost || 0) - (a.expected_cost || 0);
      }
      if (sortBy === 'expected_cost_asc') {
        return (a.expected_cost || 0) - (b.expected_cost || 0);
      }
      if (sortBy === 'actual_cost_desc') {
        return (b.actual_cost || 0) - (a.actual_cost || 0);
      }
      if (sortBy === 'actual_cost_asc') {
        return (a.actual_cost || 0) - (b.actual_cost || 0);
      }
      if (sortBy === 'diff_cost_desc') {
        return (b.cost_difference || 0) - (a.cost_difference || 0);
      }
      if (sortBy === 'diff_cost_asc') {
        return (a.cost_difference || 0) - (b.cost_difference || 0);
      }
      return 0;
    });

  const summary = useMemo(() => {
    return filteredData.reduce(
      (acc, item) => {
        acc.totalItems += 1;
        acc.totalExpectedCost += item.expected_cost || 0;
        acc.totalActualCost += item.actual_cost || 0;
        acc.totalCostDifference += item.cost_difference || 0;
        acc.totalDamageCost += item.total_damage_cost || 0;
        return acc;
      },
      {
        totalItems: 0,
        totalExpectedCost: 0,
        totalActualCost: 0,
        totalCostDifference: 0,
        totalDamageCost: 0,
      }
    );
  }, [filteredData]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tCommon('Consumption')}
          <span className={styles.menuTitle}>{tCommon('Efficiency')}</span>
        </h2>
      </div>

      {subscriptionType && subscriptionType === SUBSCRIPTION_TYPE.BASIC && (
        <RestrictionInfo
          title={tCommon('Feature not available')}
          content={tCommon('To get stock report options upgrade subscription to premium')}
        />
      )}

      {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.BASIC && (
        <>
          <div className={styles.changeable}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12}>
                <Typography variant="h6">
                  {tReport('Report from {{fromDate}} to {{toDate}}', {
                    fromDate: DateTimeHelpers.convertDateToDMY(fromDate),
                    toDate: DateTimeHelpers.convertDateToDMY(toDate),
                  })}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} md={isWarehouseActive ? 2 : 2.4}>
                <DateTimeRange
                  setFilterFromDate={setFromDate}
                  setFilterToDate={setToDate}
                  storageKey="SUBITEM_CONSUMPTION_REPORT"
                />
              </Grid>
              <Grid item xs={12} sm={4} md={isWarehouseActive ? 2 : 2.4}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label={tCommon('Search items')}
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </Grid>
              {isWarehouseActive && (
                <Grid item xs={12} sm={4} md={2}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={tCommon('Kitchen') || 'Kitchen'}
                    value={selectedKitchenId}
                    onChange={(e) => setSelectedKitchenId(e.target.value)}
                  >
                    <MenuItem value="">{tCommon('All Kitchens') || 'All Kitchens'}</MenuItem>
                    {kitchens.map((k) => (
                      <MenuItem key={k.id} value={String(k.id)}>
                        {k.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={12} sm={4} md={isWarehouseActive ? 2 : 2.4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  variant="outlined"
                  label={tCommon('Efficiency Filter')}
                  value={efficiencyFilter}
                  onChange={(e) => setEfficiencyFilter(e.target.value)}
                >
                  <MenuItem value="all">{tCommon('All Status')}</MenuItem>
                  <MenuItem value="within">{tCommon('Within Efficiency')}</MenuItem>
                  <MenuItem value="outside">{tCommon('Outside Efficiency')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4} md={isWarehouseActive ? 2 : 2.4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  variant="outlined"
                  label={tCommon('Sort By')}
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="name_asc">{tCommon('Name (A-Z)')}</MenuItem>
                  <MenuItem value="name_desc">{tCommon('Name (Z-A)')}</MenuItem>
                  <MenuItem value="expected_desc">{tCommon('Expected Consumption (High to Low)')}</MenuItem>
                  <MenuItem value="expected_asc">{tCommon('Expected Consumption (Low to High)')}</MenuItem>
                  <MenuItem value="actual_desc">{tCommon('Actual Consumption (High to Low)')}</MenuItem>
                  <MenuItem value="actual_asc">{tCommon('Actual Consumption (Low to High)')}</MenuItem>
                  <MenuItem value="diff_desc">{tCommon('Difference (High to Low)')}</MenuItem>
                  <MenuItem value="diff_asc">{tCommon('Difference (Low to High)')}</MenuItem>
                  <MenuItem value="expected_cost_desc">{tCommon('Expected Cost (High to Low)') || 'Expected Cost (High to Low)'}</MenuItem>
                  <MenuItem value="expected_cost_asc">{tCommon('Expected Cost (Low to High)') || 'Expected Cost (Low to High)'}</MenuItem>
                  <MenuItem value="actual_cost_desc">{tCommon('Actual Cost (High to Low)') || 'Actual Cost (High to Low)'}</MenuItem>
                  <MenuItem value="actual_cost_asc">{tCommon('Actual Cost (Low to High)') || 'Actual Cost (Low to High)'}</MenuItem>
                  <MenuItem value="diff_cost_desc">{tCommon('Cost Difference (High to Low)') || 'Cost Difference (High to Low)'}</MenuItem>
                  <MenuItem value="diff_cost_asc">{tCommon('Cost Difference (Low to High)') || 'Cost Difference (Low to High)'}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4} md={isWarehouseActive ? 2 : 2.4}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', width: '100%' }}>
                  <div style={{ paddingBottom: '4px' }}>
                    <label className={styles.label}>{tCommon('Generate Report')}</label>
                  </div>

                  <Button
                    variant="contained"
                    fullWidth
                    style={{
                      backgroundColor: '#00a65a',
                      color: '#fff',
                      height: 40,
                    }}
                    onClick={generateReport}
                    disabled={isShowGenerateButton === false || loadingIndicator}
                    startIcon={loadingIndicator ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                  >
                    {tCommon('Generate')}
                  </Button>
                </div>
              </Grid>
            </Grid>
          </div>

          {isShowGenerateButton === null ? (
            <Info
              title={tReport('Set filters and click generate button')}
              content={tReport(
                'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
              )}
            />
          ) : filteredData && filteredData.length ? (
            <div style={{ marginTop: 20, position: 'relative' }}>
              <Grayout open={isShowGenerateButton} />

              {filteredData && filteredData.length > 0 && (
                <div className={styles.summaryContainer} style={{ marginBottom: 16 }}>
                  <div className={`${styles.summaryCard} ${styles.cardBlue}`}>
                    <span className={styles.cardTitle}>{tCommon('Total Sub-items') || 'Total Sub-items'}</span>
                    <div className={styles.cardValue}>{summary.totalItems}</div>
                  </div>

                  <div className={`${styles.summaryCard} ${styles.cardOrange}`}>
                    <span className={styles.cardTitle}>{tCommon('Total Expected Cost') || 'Total Expected Cost'}</span>
                    <div className={styles.cardValue}>{`${shop?.currency || ''} ${roundOff(summary.totalExpectedCost)}`}</div>
                  </div>

                  <div className={`${styles.summaryCard} ${styles.cardPurple}`}>
                    <span className={styles.cardTitle}>{tCommon('Total Actual Cost') || 'Total Actual Cost'}</span>
                    <div className={styles.cardValue}>{`${shop?.currency || ''} ${roundOff(summary.totalActualCost)}`}</div>
                    {summary.totalDamageCost > 0 && (
                      <div className={styles.cardDetails}>
                        <span style={{ color: '#E53935' }}>{tCommon('Damage Cost') || 'Damage Cost'}:</span>
                        <span className={styles.detailsValue} style={{ color: '#E53935' }}>
                          {`${shop?.currency || ''} ${roundOff(summary.totalDamageCost)}`}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className={`${styles.summaryCard} ${summary.totalCostDifference >= 0 ? styles.cardGreen : styles.cardRed}`}>
                    <span className={styles.cardTitle}>{tCommon('Total Cost Difference') || 'Total Cost Difference'}</span>
                    <div 
                      className={styles.cardValue} 
                      style={{ 
                        color: summary.totalCostDifference >= 0 ? '#00a65a' : '#E53935' 
                      }}
                    >
                      {summary.totalCostDifference < 0
                        ? `-${shop?.currency || ''} ${roundOff(Math.abs(summary.totalCostDifference))}`
                        : `${shop?.currency || ''} ${roundOff(summary.totalCostDifference)}`}
                    </div>
                  </div>
                </div>
              )}
              
              <Grid container spacing={1}>
                {filteredData.map((item) => {
                  const isWithinEfficiency = getIsWithinEfficiency(item);
                  const borderCol = isWithinEfficiency ? '#3F51B5' : '#E53935';
                  const isExpanded = !!openCardId[item.subitem_id];
                  const kitchenNames = isWarehouseActive
                    ? Array.from(
                        new Set((item.combo_items || []).map((c) => c.kitchen?.name).filter(Boolean))
                      ).join(', ')
                    : '';
                  
                  return (
                    <Grid item xs={12} key={item.subitem_id}>
                      <Card 
                        style={{ 
                          borderLeft: `3px solid ${borderCol}`,
                          borderTop: `1px solid ${borderCol}40`,
                          borderRight: `1px solid ${borderCol}40`,
                          borderBottom: `1px solid ${borderCol}40`,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                          borderRadius: 6
                        }}
                      >
                        <CardContent style={{ padding: '8px 16px' }} sx={{ '&:last-child': { pb: '8px' } }}>
                          <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12} sm={6} md={2.4}>
                              <Typography variant="subtitle1" style={{ fontWeight: 600, fontSize: '1.05rem', lineHeight: '1.2' }}>
                                {item.subitem_name}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.85rem' }}>
                                {tCommon('Unit')}: {item.subitem_unit || 'N/A'}{item.subitem_cost ? ` (${shop?.currency || ''} ${roundOff(item.subitem_cost)}/${item.subitem_unit || 'unit'})` : ''}
                              </Typography>
                              {isWarehouseActive && (
                                <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.85rem' }}>
                                  {tCommon('Kitchen') || 'Kitchen'}: {kitchenNames || 'N/A'}
                                </Typography>
                              )}
                            </Grid>
                            
                            <Grid item xs={6} sm={3} md={2}>
                              <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.75rem', lineHeight: '1.1' }}>
                                {tCommon('Expected Consumption')}
                              </Typography>
                              <Typography variant="body1" style={{ fontWeight: 600, fontSize: '0.95rem', lineHeight: '1.2' }}>
                                {roundOff(item.total_expected)} {item.subitem_unit}
                              </Typography>
                              <Typography variant="caption" display="block" color="textSecondary" style={{ fontSize: '0.75rem' }}>
                                {tCommon('Cost')}: {shop?.currency || ''} {roundOff(item.expected_cost)}
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={6} sm={3} md={2}>
                              <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.75rem', lineHeight: '1.1' }}>
                                {tCommon('Actual Consumption')}
                              </Typography>
                              <Typography variant="body1" style={{ fontWeight: 600, fontSize: '0.95rem', lineHeight: '1.2' }}>
                                {roundOff(item.total_used)} {item.subitem_unit}
                              </Typography>
                              <Typography variant="caption" display="block" color="textSecondary" style={{ fontSize: '0.75rem' }}>
                                {tCommon('Cost')}: {shop?.currency || ''} {roundOff(item.actual_cost)}
                              </Typography>
                              {item.total_damage > 0 && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  style={{ fontSize: '0.75rem', color: '#E53935', fontWeight: 600 }}
                                >
                                  {tCommon('Damage') || 'Damage'}: {roundOff(item.total_damage)} {item.subitem_unit}
                                  {item.total_damage_cost > 0 ? ` (${shop?.currency || ''} ${roundOff(item.total_damage_cost)})` : ''}
                                </Typography>
                              )}
                            </Grid>
                            
                            <Grid item xs={6} sm={3} md={2}>
                              <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.75rem', lineHeight: '1.1' }}>
                                {tCommon('Difference')}
                              </Typography>
                              <Typography 
                                variant="body1" 
                                style={{ 
                                  fontWeight: 600,
                                  fontSize: '0.95rem',
                                  lineHeight: '1.2',
                                  color: item.difference >= 0 ? '#00a65a' : '#E53935'
                                }}
                              >
                                {roundOff(item.difference)} {item.subitem_unit}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                display="block"
                                style={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  color: item.cost_difference >= 0 ? '#00a65a' : '#E53935'
                                }}
                              >
                                {tCommon('Cost')}: {item.cost_difference < 0 ? `-${shop?.currency || ''} ${roundOff(Math.abs(item.cost_difference))}` : `${shop?.currency || ''} ${roundOff(item.cost_difference)}`}
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={6} sm={6} md={2.8}>
                              <div 
                                style={{ 
                                  display: 'inline-block',
                                  padding: '3px 10px',
                                  borderRadius: 12,
                                  backgroundColor: isWithinEfficiency ? '#3F51B520' : '#E5393520',
                                  color: borderCol,
                                  fontWeight: 600,
                                  fontSize: '0.8rem',
                                  textAlign: 'center'
                                }}
                              >
                                {isWithinEfficiency
                                  ? (tCommon('Consumption within efficiency limit') || 'Consumption within efficiency limit')
                                  : (tCommon('Consumption outside efficiency limit') || 'Consumption outside efficiency limit')}
                              </div>
                            </Grid>
                            
                            <Grid item xs={12} sm={3} md={0.8} style={{ textAlign: 'right' }}>
                              <IconButton size="small" onClick={() => toggleCard(item.subitem_id)}>
                                {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                              </IconButton>
                            </Grid>
                          </Grid>

                          <Collapse in={isExpanded} timeout="auto" unmountOnExit style={{ marginTop: 15 }}>
                            <Typography variant="subtitle2" color="textSecondary" style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 8 }}>
                              {tCommon('Used in following items')}:
                            </Typography>
                            <TableContainer component={Paper} variant="outlined" style={{ marginBottom: item.consumptions && item.consumptions.length > 0 ? 16 : 0 }}>
                              <Table size="small">
                                <TableHead style={{ backgroundColor: '#f5f5f5' }}>
                                  <TableRow>
                                    <TableCell style={{ fontWeight: 600 }}>{tCommon('Item Name')}</TableCell>
                                    {isWarehouseActive && (
                                      <TableCell style={{ fontWeight: 600 }}>{tCommon('Kitchen') || 'Kitchen'}</TableCell>
                                    )}
                                    <TableCell align="right" style={{ fontWeight: 600 }}>{tCommon('Quantity Sold')}</TableCell>
                                    <TableCell align="right" style={{ fontWeight: 600 }}>{tCommon('Ratio / Fraction')}</TableCell>
                                    <TableCell align="right" style={{ fontWeight: 600 }}>{tCommon('Expected Consumption')}</TableCell>
                                    <TableCell align="right" style={{ fontWeight: 600 }}>{tCommon('Expected Cost') || 'Expected Cost'}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {item.combo_items && item.combo_items.map((combo, idx) => (
                                    <TableRow key={combo.combo_id ? `${combo.combo_id}_${idx}` : idx}>
                                      <TableCell>{combo.combo_name}</TableCell>
                                      {isWarehouseActive && (
                                        <TableCell>{combo.kitchen?.name || 'N/A'}</TableCell>
                                      )}
                                      <TableCell align="right">{roundOff(combo.quantity_sold)}</TableCell>
                                      <TableCell align="right">{combo.fraction} {item.subitem_unit}</TableCell>
                                      <TableCell align="right">{roundOff(combo.expected_consumption)} {item.subitem_unit}</TableCell>
                                      <TableCell align="right">
                                        {shop?.currency || ''} {roundOff((parseFloat(combo.expected_consumption) || 0) * (item.subitem_cost || 0))}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  {(!item.combo_items || item.combo_items.length === 0) && (
                                    <TableRow>
                                      <TableCell colSpan={isWarehouseActive ? 6 : 5} align="center">
                                        {tCommon('No combo items mapped')}
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>

                            {item.consumptions && item.consumptions.length > 0 && (
                              <>
                                <Typography variant="subtitle2" color="textSecondary" style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 8 }}>
                                  {tCommon('Warehouse Stock Logs (Consumption & Damage)') || 'Warehouse Stock Logs (Consumption & Damage)'}:
                                </Typography>
                                <TableContainer component={Paper} variant="outlined">
                                  <Table size="small">
                                    <TableHead style={{ backgroundColor: '#f5f5f5' }}>
                                      <TableRow>
                                        <TableCell style={{ fontWeight: 600 }}>{tCommon('Warehouse') || 'Warehouse'}</TableCell>
                                        <TableCell align="right" style={{ fontWeight: 600 }}>{tCommon('Consumed Quantity') || 'Consumed Quantity'}</TableCell>
                                        <TableCell align="right" style={{ fontWeight: 600 }}>{tCommon('Consumed Cost') || 'Consumed Cost'}</TableCell>
                                        <TableCell align="right" style={{ fontWeight: 600 }}>{tCommon('Damage Quantity') || 'Damage Quantity'}</TableCell>
                                        <TableCell align="right" style={{ fontWeight: 600 }}>{tCommon('Damage Value') || 'Damage Value'}</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {item.consumptions.map((c, idx) => (
                                        <TableRow key={c.warehouse_id ? `wh_${c.warehouse_id}_${idx}` : idx}>
                                          <TableCell>{getWarehouseName(c.warehouse_id)}</TableCell>
                                          <TableCell align="right">{roundOff(c.consumed_qty)} {item.subitem_unit}</TableCell>
                                          <TableCell align="right">
                                            {shop?.currency || ''} {roundOff((parseFloat(c.consumed_qty) || 0) * (item.subitem_cost || 0))}
                                          </TableCell>
                                          <TableCell 
                                            align="right" 
                                            style={{ 
                                              color: (parseFloat(c.damage_qty) || 0) > 0 ? '#E53935' : 'inherit', 
                                              fontWeight: (parseFloat(c.damage_qty) || 0) > 0 ? 600 : 'normal' 
                                            }}
                                          >
                                            {roundOff(c.damage_qty)} {item.subitem_unit}
                                          </TableCell>
                                          <TableCell 
                                            align="right" 
                                            style={{ 
                                              color: (parseFloat(c.damage_value) || 0) > 0 ? '#E53935' : 'inherit', 
                                              fontWeight: (parseFloat(c.damage_value) || 0) > 0 ? 600 : 'normal' 
                                            }}
                                          >
                                            {shop?.currency || ''} {roundOff(c.damage_value)}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </>
                            )}
                          </Collapse>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </div>
          ) : (
            !loadingIndicator && (
              <Info
                title={tReport('Oh no, there are data matching to your filter')}
                content={tReport(
                  'There are no data matching to your filter. Please verify your inputs to generate report'
                )}
              />
            )
          )}
        </>
      )}
    </div>
  );
}

export default withConsoleBase(SubitemConsumptionReport);
